/*
 * aura-wishlist-rebind.js
 *
 * THE BUG (found 2 Sep 2026): assets/wishlist.js binds a click listener to every
 * `.button-wishlist` ONCE, on DOMContentLoaded. Any card that arrives later has an
 * inert heart. That is most of the catalogue in practice - everything appended by
 * "View More", everything re-appended by the Back restore in aura-collection.js, and
 * the product cards injected into "You may also like" and "Recently viewed" on a PDP.
 * Clicking those hearts did nothing at all: no storage write, no active state, and
 * nothing in the wishlist afterwards.
 *
 * THE FIX: bind the ones the theme missed, and ONLY those.
 *
 * ⚠️ Deliberately NOT document-level delegation. wishlist.js already has a listener
 * on every button it binds, so a delegated handler would fire alongside it and toggle
 * the item twice - add then immediately remove - which looks exactly like the original
 * bug. Ownership is tracked with `data-aura-wish-bound`: wishlist.js stamps "theme" as
 * it binds, this file stamps "aura", and neither ever binds a button that already
 * carries the attribute.
 *
 * ⚠️ The marker MUST be written by wishlist.js at bind time, not swept on here at
 * startup. setupGrid() is async: on /pages/wishlist it renders the cards and binds them
 * long after this file has run, so a one-shot sweep marked nothing and the observer
 * below then bound every one of them a second time. Symptom: clicking the X removed
 * nothing and the handle merely moved to the end of the stored list.
 */
(function () {
  var KEY = 'shopify-wishlist';
  var DELIM = ',';
  var ACTIVE = 'active';

  function saved() {
    try {
      return (localStorage.getItem(KEY) || '').split(DELIM).filter(Boolean);
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      if (list.length) localStorage.setItem(KEY, list.join(DELIM));
      else localStorage.removeItem(KEY);
    } catch (e) { /* private mode - the click simply will not persist */ }
  }

  function toggle(handle) {
    var list = saved();
    var i = list.indexOf(handle);
    if (i === -1) list.push(handle); else list.splice(i, 1);
    write(list);
  }

  // Repaint EVERY control from storage, whoever bound it. wishlist.js only updates
  // the one button clicked, so without this the heart and the "Add to wishlist" link
  // on a PDP disagree with each other.
  function paint() {
    var list = saved();
    document.querySelectorAll('.button-wishlist[data-product-handle]').forEach(function (b) {
      var on = list.indexOf(b.dataset.productHandle) !== -1;
      b.classList.toggle(ACTIVE, on);
      var no = b.querySelector('.icon.not-added');
      var yes = b.querySelector('.icon.added');
      // clear wishlist.js's inline display so the stylesheet governs again
      if (no) no.style.display = '';
      if (yes) yes.style.display = '';
    });
  }

  function bindNew() {
    document.querySelectorAll('.button-wishlist:not([data-aura-wish-bound])').forEach(function (b) {
      b.setAttribute('data-aura-wish-bound', 'aura');
      b.addEventListener('click', function (e) {
        var handle = b.dataset.productHandle;
        if (!handle) return;
        // these hearts sit inside the card's link on a collection card
        e.preventDefault();
        e.stopPropagation();
        toggle(handle);
        paint();
        document.dispatchEvent(new CustomEvent('shopify-wishlist:updated'));
      });
    });
    paint();
  }

  function start() {
    // Runs after wishlist.js's own DOMContentLoaded listener, because this file is
    // loaded after it and listeners fire in registration order. So anything the theme
    // was going to bind at load already carries data-aura-wish-bound="theme" and is
    // skipped here. The initial bindNew() is not redundant: wishlist.js guards its load
    // pass with `if (!document.querySelector('.related-products')) initButtons()`, so on
    // a page containing that element NOTHING is bound at load and this is the only pass
    // that binds anything.
    bindNew();

    var host = document.body || document.documentElement;
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(bindNew, 120);
    }).observe(host, { childList: true, subtree: true });

    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.button-wishlist')) setTimeout(paint, 0);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
